import type { GridRowClassNameParams } from '@mui/x-data-grid-pro';
import type { ItemStubsResponseDTO } from '@tempo-io/roadmap-dto';
import type { Item } from '@/common/api/types/items';
import type { DependencyForecastDates } from '@/features/item-panel/components/hooks/useDependencyForecastDates';
import type {
  DependencySectionValue,
  ItemWithSchedule,
} from '@/features/item-panel/components/tabs/dependencies/lib/types';
import type { ItemsTableRow } from '@/features/items/components/table/types';

export const toItemStubs = (rows: ItemWithSchedule[]): ItemStubsResponseDTO =>
  rows.map((row) => ({ id: row.id, matched: true }));

export const withSchedules = (
  items: Item[],
  forecastDates: Record<number, DependencyForecastDates>,
): ItemWithSchedule[] =>
  items.map((dep) => ({
    ...dep,
    forecastedStartDateTime: forecastDates[dep.id]?.forecastedStartDateTime ?? null,
    forecastedFinishDateTime: forecastDates[dep.id]?.forecastedFinishDateTime ?? null,
  }));

export const sortByDate = (
  items: ItemWithSchedule[],
  dateField: 'forecastedStartDateTime' | 'forecastedFinishDateTime',
  sortDirection: 1 | -1,
): ItemWithSchedule[] =>
  [...items].sort((a, b) => {
    const aDate = a[dateField];
    const bDate = b[dateField];
    if (aDate === null && bDate === null) return 0;
    if (aDate === null) return -1;
    if (bDate === null) return 1;
    return aDate < bDate ? -sortDirection : aDate > bDate ? sortDirection : 0;
  });

export const blockedRowClassName =
  (blockedIds: Set<number>) => (params: GridRowClassNameParams<ItemsTableRow>) =>
    blockedIds.has(Number(params.id))
      ? 'group bg-bg-error-subtler hover:bg-bg-error-subtler-hovered'
      : 'group';

export const dependencyPairFor = (
  section: DependencySectionValue,
  currentItemId: number,
  otherItemId: number,
): [predecessorId: number, successorId: number] =>
  section === 'predecessors' ? [otherItemId, currentItemId] : [currentItemId, otherItemId];

export const isParentChildRejection = (serverMessage: string | null): boolean =>
  serverMessage?.includes('cannot be in a parent-child relationship') ?? false;
